import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Typography,
  Button,
  Upload,
  message,
  Row,
  Col,
  Modal,
  Input,
  Empty,
  Dropdown,
  Menu,
  Progress,
  Checkbox,
} from "antd";
import {
  Image as ImageIcon,
  Video,
  FilePlus,
  FolderOpen,
  Trash2,
  Search,
  Grid3x3,
  List,
  Plus,
  Eye,
  ListPlus,
  MoreVertical,
  CheckCircle2,
  Pencil,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import LiteAPI from "../api/liteAPI";
import { useLanguage, UI_TRANSLATIONS } from "../LanguageContext";

const { Title, Text } = Typography;
const { Dragger } = Upload;

const FILE_SIZE_LIMIT = 1 * 1024 * 1024 * 1024; // 1 GB in bytes

const ACCEPTED_MIME_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/gif",
  "video/mp4",
]);

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1) return `${gb.toFixed(2)} GB`;
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

const stagger = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.05 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
};

const ContentLibrary = ({
  libraryList,
  setLibraryList,
  playlistItems,
  setPlaylistItems,
  apiProps,
  brandData,
  playlistId,
  refreshLibrary,
  refreshPlaylist,
}) => {
  const { lang } = useLanguage();
  const t = UI_TRANSLATIONS[lang]?.library ?? UI_TRANSLATIONS.en.library;

  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [previewItem, setPreviewItem] = useState(null);
  const [renameItem, setRenameItem] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameLoading, setRenameLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // null = not uploading, 0-100 = uploading
  const [accountStorage, setAccountStorage] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const blockedFiles = useRef(new Set());
  const probedIds = useRef(new Set());
  const thumbnailedIds = useRef(new Set());

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredList.map((item) => item.id)));
    }
  };

  const handleBulkAddToLoop = () => {
    const items = filteredList.filter((item) => selectedIds.has(item.id));
    Modal.confirm({
      title: "Add to Loop",
      content: `Add ${items.length} file${items.length > 1 ? "s" : ""} to the loop playlist?`,
      okText: "Add All",
      okButtonProps: {
        style: { background: "#6366f1", borderColor: "#6366f1" },
      },
      onOk: async () => {
        for (const item of items) {
          await handleAddToPlaylist(item);
        }
        setSelectedIds(new Set());
      },
    });
  };

  const handleBulkDelete = () => {
    const items = filteredList.filter((item) => selectedIds.has(item.id));
    Modal.confirm({
      title: t.deleteTitle,
      content: `${items.length} files will be deleted.`,
      okText: t.deleteOk,
      okButtonProps: { danger: true },
      onOk: async () => {
        for (const item of items) {
          if (apiProps && brandData) {
            try {
              const fileId = item.raw?.FileID || item.id;
              await LiteAPI.deleteFile(fileId, brandData.BrandID, apiProps);
            } catch (err) {
              console.error("Bulk delete error:", err);
            }
          } else {
            setLibraryList((prev) => prev.filter((f) => f.id !== item.id));
          }
        }
        message.success(t.deleteSuccess);
        setSelectedIds(new Set());
        if (refreshLibrary) await refreshLibrary();
        if (refreshPlaylist) await refreshPlaylist();
      },
    });
  };

  const applyDuration = (itemId, secs) => {
    if (!secs || isNaN(secs)) return;
    const fmt =
      secs >= 60 ? `${Math.floor(secs / 60)}m ${secs % 60}s` : `${secs}s`;
    setLibraryList((prev) =>
      prev.map((li) =>
        li.id === itemId ? { ...li, duration: fmt, durationSeconds: secs } : li,
      ),
    );
    setPreviewItem((prev) =>
      prev && prev.id === itemId
        ? { ...prev, duration: fmt, durationSeconds: secs }
        : prev,
    );
  };

  const handleVideoMetadataLoaded = (itemId, e) => {
    const secs = Math.round(e.target.duration);
    applyDuration(itemId, secs);
  };

  // Probe duration via hidden video for items whose duration is still the default 10s
  useEffect(() => {
    libraryList.forEach((item) => {
      if (item.type !== "video") return;
      const src = item.url || item.previewUrl;
      if (!src) return;

      // Duration probe — only run if server has no Duration stored
      if (!probedIds.current.has(item.id) && !item.raw?.Duration) {
        probedIds.current.add(item.id);
        const vid = document.createElement("video");
        vid.preload = "metadata";
        vid.onloadedmetadata = () => {
          applyDuration(item.id, Math.round(vid.duration));
        };
        vid.src = src;
      }
    });
  }, [libraryList]);

  const refreshAccountStorage = () => {
    if (!apiProps || !brandData?.BrandID) return;
    LiteAPI.getAccountStorage(brandData.BrandID, apiProps.usertoken)
      .then((data) => {
        if (data) setAccountStorage(data);
      })
      .catch((err) =>
        console.warn("[ContentLibrary] Storage fetch failed:", err),
      );
  };

  useEffect(() => {
    refreshAccountStorage();
  }, [brandData?.BrandID]);

  const filteredList = libraryList.filter((item) =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const isAllSelected =
    filteredList.length > 0 &&
    filteredList.every((item) => selectedIds.has(item.id));
  const isIndeterminate =
    !isAllSelected && filteredList.some((item) => selectedIds.has(item.id));

  // storageUsed is derived from libraryList — updates instantly on add/delete
  const storageUsed = useMemo(
    () => libraryList.reduce((sum, f) => sum + (Number(f.sizeBytes) || 0), 0),
    [libraryList],
  );
  const storageTotal = accountStorage?.storagebyte?.totalspace ?? null;

  const beforeUpload = (file) => {
    if (!ACCEPTED_MIME_TYPES.has(file.type)) {
      message.error(t.invalidType(file.name));
      blockedFiles.current.add(file.uid);
      return false;
    }
    if (file.size > FILE_SIZE_LIMIT) {
      message.error(t.sizeExceeds(file.name));
      blockedFiles.current.add(file.uid);
      return false;
    }
    if (storageTotal !== null) {
      if (storageUsed + file.size > storageTotal) {
        const remaining = storageTotal - storageUsed;
        message.error(
          t.storageInsufficient(formatBytes(remaining), formatBytes(file.size)),
        );
        blockedFiles.current.add(file.uid);
        return false;
      }
    }
    return true;
  };

  const handleUpload = async (info) => {
    if (info.file.status === "uploading") return;

    // Skip files that were blocked by beforeUpload
    if (blockedFiles.current.has(info.file.uid)) {
      blockedFiles.current.delete(info.file.uid);
      return;
    }

    const file = info.file.originFileObj || info.file;

    if (apiProps && brandData) {
      // ── Real upload via API ──
      try {
        setUploadProgress(0);
        await LiteAPI.uploadFile(
          file,
          brandData.BrandID,
          apiProps,
          (percent) => {
            setUploadProgress(percent);
          },
        );
        setUploadProgress(null);
        message.success(t.uploadSuccess(file.name));
        // Refresh library — storageUsed updates automatically from libraryList
        if (refreshLibrary) await refreshLibrary();
      } catch (err) {
        setUploadProgress(null);
        message.error(t.uploadFail(file.name));
        console.error("Upload error:", err);
      }
    } else {
      // ── Fallback: local mockup mode ──
      if (info.file.status === "done") {
        const newFile = {
          id: "lib-" + Date.now().toString(),
          name: info.file.name,
          type:
            info.file.type && info.file.type.includes("video")
              ? "video"
              : "image",
          duration: "10s",
          size: (info.file.size / (1024 * 1024)).toFixed(1) + " MB",
          uploadedAt: "Just now",
          url: info.file.originFileObj
            ? URL.createObjectURL(info.file.originFileObj)
            : null,
          previewUrl: info.file.originFileObj
            ? URL.createObjectURL(info.file.originFileObj)
            : null,
        };
        setLibraryList([newFile, ...libraryList]);
        message.success(t.uploadSuccess(info.file.name));
      }
    }
  };

  const dummyRequest = ({ file, onSuccess }) => {
    // For local mockup mode — immediately succeed
    setTimeout(() => onSuccess("ok"), 500);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: t.deleteTitle,
      content: t.deleteContent,
      okText: t.deleteOk,
      okButtonProps: { danger: true },
      onOk: async () => {
        if (apiProps && brandData) {
          // ── Real delete via API ──
          try {
            const item = libraryList.find((f) => f.id === id);
            const fileId = item?.raw?.FileID || id;
            await LiteAPI.deleteFile(fileId, brandData.BrandID, apiProps);
            message.success(t.deleteSuccess);
            if (refreshLibrary) await refreshLibrary();
            if (refreshPlaylist) await refreshPlaylist();
          } catch (err) {
            message.error(t.deleteFail);
            console.error("Delete error:", err);
          }
        } else {
          // ── Fallback: local mockup mode ──
          setLibraryList(libraryList.filter((item) => item.id !== id));
          setPlaylistItems(playlistItems.filter((item) => item.id !== id));
          message.success(t.deleteSuccess);
        }
      },
    });
  };

  const openRename = (item) => {
    setRenameItem(item);
    setRenameValue(item.name);
  };

  const closeRename = () => {
    setRenameItem(null);
    setRenameValue("");
  };

  const handleRenameConfirm = async () => {
    const newName = renameValue.trim();
    if (!renameItem || !newName) {
      message.error(t.renameEmpty);
      return;
    }
    if (newName === renameItem.name) {
      closeRename();
      return;
    }

    setRenameLoading(true);
    try {
      if (apiProps && brandData) {
        // ── Real rename via API ──
        const fileId = renameItem.raw?.FileID || renameItem.id;
        await LiteAPI.renameFile(fileId, newName, apiProps);
        if (refreshLibrary) await refreshLibrary();
      } else {
        // ── Fallback: local mockup mode ──
        setLibraryList((prev) =>
          prev.map((li) =>
            li.id === renameItem.id ? { ...li, name: newName } : li,
          ),
        );
      }
      message.success(t.renameSuccess);
      closeRename();
    } catch (err) {
      message.error(t.renameFail);
      console.error("Rename error:", err);
    } finally {
      setRenameLoading(false);
    }
  };

  const handleAddToPlaylist = async (item) => {
    if (apiProps && brandData && playlistId) {
      // ── Real add via API ──
      try {
        const fileId = item.raw?.FileID || item.id;
        await LiteAPI.addMediaToPlaylist(
          playlistId,
          fileId,
          brandData.BrandID,
          apiProps,
        );
        message.success(t.addSuccess(item.name));
        if (refreshPlaylist) await refreshPlaylist();
      } catch (err) {
        message.error(t.addFail);
        console.error("Add to playlist error:", err);
      }
    } else {
      // ── Fallback: local mockup mode ──
      const newItem = {
        ...item,
        id: "pl-" + Date.now() + Math.random().toString(36).substr(2, 4),
      };
      setPlaylistItems([...playlistItems, newItem]);
      message.success(t.addSuccess(item.name));
    }
  };

  const isInPlaylist = (itemId) => {
    return playlistItems.some(
      (p) => p.name === libraryList.find((l) => l.id === itemId)?.name,
    );
  };

  const getItemMenu = (item) => (
    <Menu>
      <Menu.Item
        key="preview"
        icon={<Eye size={14} />}
        onClick={() => setPreviewItem(item)}
      >
        {t.preview}
      </Menu.Item>
      <Menu.Item
        key="rename"
        icon={<Pencil size={14} />}
        onClick={() => openRename(item)}
      >
        {t.rename}
      </Menu.Item>
      <Menu.Item
        key="add"
        icon={<ListPlus size={14} />}
        onClick={() => handleAddToPlaylist(item)}
      >
        {t.addToPlaylist}
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item
        key="delete"
        icon={<Trash2 size={14} />}
        danger
        onClick={() => handleDelete(item.id)}
      >
        {t.delete}
      </Menu.Item>
    </Menu>
  );

  return (
    <motion.div variants={stagger} initial="hidden" animate="show">
      {/* ── Header ── */}
      <motion.div variants={fadeUp} className="lite-section-header">
        <div>
          <h2>{t.title}</h2>
          <p>{t.subtitle}</p>
        </div>
      </motion.div>

      {/* ── Upload Zone ── */}
      <motion.div variants={fadeUp} style={{ marginBottom: "28px" }}>
        <Dragger
          name="file"
          multiple={true}
          accept=".jpg,.jpeg,.png,.gif,.mp4"
          customRequest={apiProps && brandData ? undefined : dummyRequest}
          action={apiProps && brandData ? undefined : undefined}
          beforeUpload={beforeUpload}
          onChange={handleUpload}
          showUploadList={false}
          className="lite-upload-zone"
          style={{
            borderRadius: "16px",
            border: "2px dashed #cbd5e1",
            background: "#fafbfc",
            padding: "28px 0",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <div
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "14px",
                background: "linear-gradient(135deg, #eef2ff, #e0e7ff)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FilePlus size={28} color="#6366f1" />
            </div>
            <Text strong style={{ fontSize: "15px", color: "#0f172a" }}>
              {t.dropzone}
            </Text>
            <Text type="secondary" style={{ fontSize: "13px" }}>
              {t.dropzoneHint} •{" "}
              {storageTotal !== null
                ? `${formatBytes(storageUsed)} / ${formatBytes(storageTotal)} used`
                : `${formatBytes(storageUsed)} used`}
            </Text>
            {uploadProgress !== null && (
              <Progress
                percent={uploadProgress}
                size="small"
                style={{ width: "200px", marginTop: "4px" }}
              />
            )}
          </div>
        </Dragger>
      </motion.div>

      {/* ── Toolbar ── */}
      <motion.div
        variants={fadeUp}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Checkbox
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            onChange={toggleSelectAll}
            style={{ fontWeight: 500, fontSize: "13px" }}
          >
            {filteredList.length} {t.files}
          </Checkbox>
          <Input
            placeholder={t.searchPlaceholder}
            prefix={<Search size={16} color="#94a3b8" />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: "220px", borderRadius: "8px" }}
            allowClear
          />
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {selectedIds.size > 0 && (
            <>
              <span
                style={{ fontSize: "13px", color: "#6366f1", fontWeight: 600 }}
              >
                {selectedIds.size} selected
              </span>
              <Button
                size="small"
                icon={<ListPlus size={14} />}
                onClick={handleBulkAddToLoop}
                style={{
                  borderRadius: "8px",
                  borderColor: "#6366f1",
                  color: "#6366f1",
                  fontWeight: 600,
                }}
              >
                Add All to Loop
              </Button>
              <Button
                size="small"
                danger
                icon={<Trash2 size={14} />}
                onClick={handleBulkDelete}
                style={{ borderRadius: "8px", fontWeight: 600 }}
              >
                Delete All
              </Button>
            </>
          )}
          <Button
            type={viewMode === "grid" ? "primary" : "default"}
            icon={<Grid3x3 size={16} />}
            onClick={() => setViewMode("grid")}
            style={{
              borderRadius: "8px",
              ...(viewMode === "grid"
                ? { background: "#6366f1", borderColor: "#6366f1" }
                : {}),
            }}
          />
          <Button
            type={viewMode === "list" ? "primary" : "default"}
            icon={<List size={16} />}
            onClick={() => setViewMode("list")}
            style={{
              borderRadius: "8px",
              ...(viewMode === "list"
                ? { background: "#6366f1", borderColor: "#6366f1" }
                : {}),
            }}
          />
        </div>
      </motion.div>

      {/* ── Content ── */}
      {filteredList.length === 0 ? (
        <motion.div variants={fadeUp} className="lite-empty-state">
          <div className="icon-circle">
            <FolderOpen size={32} color="#6366f1" />
          </div>
          <Title level={4} style={{ marginBottom: "8px", color: "#0f172a" }}>
            {searchQuery ? t.noMatch : t.emptyTitle}
          </Title>
          <Text type="secondary" style={{ fontSize: "14px" }}>
            {searchQuery ? t.searchHint : t.emptyHint}
          </Text>
        </motion.div>
      ) : viewMode === "grid" ? (
        /* ── Grid View ── */
        <div className="lite-library-grid">
          <AnimatePresence>
            {console.log("filteredList", filteredList)}
            {filteredList.map((item) => (
              <motion.div
                key={item.id}
                variants={fadeUp}
                layout
                exit={{ opacity: 0, scale: 0.9 }}
                className="lite-library-item"
                style={{
                  position: "relative",
                  transition: "opacity 0.2s",
                }}
              >
                <div className="thumb" onClick={() => setPreviewItem(item)}>
                  {/* Custom checkbox — left */}
                  <div
                    style={{
                      position: "absolute",
                      top: "8px",
                      left: "8px",
                      zIndex: 4,
                      width: "22px",
                      height: "22px",
                      borderRadius: "6px",
                      background: selectedIds.has(item.id)
                        ? "#6366f1"
                        : "rgba(255,255,255,0.85)",
                      border: selectedIds.has(item.id)
                        ? "2px solid #6366f1"
                        : "2px solid rgba(200,200,210,0.9)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      backdropFilter: "blur(4px)",
                      boxShadow: selectedIds.has(item.id)
                        ? "0 2px 8px rgba(99,102,241,0.4)"
                        : "0 1px 4px rgba(0,0,0,0.12)",
                      transition: "all 0.18s ease",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelect(item.id);
                    }}
                  >
                    {selectedIds.has(item.id) && (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <polyline
                          points="2,6.5 5,9.5 10,3"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  {/* Type badge — right */}
                  <span
                    style={{
                      position: "absolute",
                      top: "8px",
                      right: "8px",
                      zIndex: 4,
                      padding: "2px 8px",
                      borderRadius: "6px",
                      fontSize: "10px",
                      fontWeight: 700,
                      letterSpacing: "0.04em",
                      background:
                        item.type === "video"
                          ? "rgba(59,130,246,0.85)"
                          : "rgba(236,72,153,0.85)",
                      color: "#fff",
                      backdropFilter: "blur(4px)",
                    }}
                  >
                    {item.type === "video" ? "VID" : "IMG"}
                  </span>
                  {/* Selected overlay */}
                  {selectedIds.has(item.id) && (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 3,
                        background: "rgba(99,102,241,0.18)",
                        pointerEvents: "none",
                      }}
                    />
                  )}
                  {item.type === "video" ? (
                    <video
                      src={item.previewUrl || item.url}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      muted
                      preload="metadata"
                      onLoadedMetadata={(e) =>
                        handleVideoMetadataLoaded(item.id, e)
                      }
                    />
                  ) : item.previewUrl ? (
                    <img src={item.previewUrl} alt={item.name} />
                  ) : (
                    <ImageIcon size={32} color="#94a3b8" />
                  )}
                </div>
                <div className="info">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ overflow: "hidden", flex: 1 }}>
                      <div className="name" title={item.name}>
                        {item.name}
                      </div>
                      <div className="meta">
                        {item.size}
                        {item.type === "video" && item.duration
                          ? ` • ${item.duration}`
                          : ""}
                      </div>
                    </div>
                    <Dropdown
                      overlay={getItemMenu(item)}
                      trigger={["click"]}
                      placement="bottomRight"
                    >
                      <Button
                        type="text"
                        size="small"
                        icon={<MoreVertical size={14} color="#94a3b8" />}
                        style={{ flexShrink: 0 }}
                      />
                    </Dropdown>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      ) : (
        /* ── List View ── */
        <motion.div
          variants={fadeUp}
          className="lite-card no-hover"
          style={{ padding: 0, overflow: "hidden" }}
        >
          <AnimatePresence>
            {filteredList.map((item, index) => (
              <motion.div
                key={item.id}
                variants={fadeUp}
                exit={{ opacity: 0, x: -20 }}
                style={{
                  padding: "14px 20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom:
                    index < filteredList.length - 1
                      ? "1px solid #f8fafc"
                      : "none",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "14px",
                    flex: 1,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: "22px",
                      height: "22px",
                      borderRadius: "6px",
                      flexShrink: 0,
                      background: selectedIds.has(item.id)
                        ? "#6366f1"
                        : "#ffffff",
                      border: selectedIds.has(item.id)
                        ? "2px solid #6366f1"
                        : "2px solid #cbd5e1",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      boxShadow: selectedIds.has(item.id)
                        ? "0 2px 8px rgba(99,102,241,0.35)"
                        : "none",
                      transition: "all 0.18s ease",
                    }}
                    onClick={() => toggleSelect(item.id)}
                  >
                    {selectedIds.has(item.id) && (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <polyline
                          points="2,6.5 5,9.5 10,3"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  <div className="lite-playlist-thumb">
                    {item.type === "video" ? (
                      <video
                        src={item.previewUrl || item.url}
                        muted
                        preload="metadata"
                        onLoadedMetadata={(e) =>
                          handleVideoMetadataLoaded(item.id, e)
                        }
                      />
                    ) : item.previewUrl ? (
                      <img src={item.previewUrl} alt={item.name} />
                    ) : (
                      <ImageIcon size={20} color="#ec4899" />
                    )}
                  </div>
                  <div style={{ overflow: "hidden" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <Text
                        strong
                        style={{
                          fontSize: "13px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.name}
                      </Text>
                      <Pencil
                        size={12}
                        color="#94a3b8"
                        style={{ flexShrink: 0, cursor: "pointer" }}
                        onClick={() => openRename(item)}
                      />
                    </div>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      {item.size}
                      {item.type === "video" && item.duration
                        ? ` • ${item.duration}`
                        : ""}
                      {item.uploadedAt ? ` • ${item.uploadedAt}` : ""}
                    </Text>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                  <Button
                    size="small"
                    icon={<Plus size={14} />}
                    onClick={() => handleAddToPlaylist(item)}
                    style={{ borderRadius: "6px", fontSize: "12px" }}
                  >
                    {t.addToLoop}
                  </Button>
                  <Dropdown
                    overlay={getItemMenu(item)}
                    trigger={["click"]}
                    placement="bottomRight"
                  >
                    <Button
                      type="text"
                      size="small"
                      icon={<MoreVertical size={14} color="#94a3b8" />}
                    />
                  </Dropdown>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      {/* ── Preview Modal ── */}
      <Modal
        title={previewItem?.name || t.preview}
        open={!!previewItem}
        onCancel={() => setPreviewItem(null)}
        footer={[
          <Button
            key="add"
            type="primary"
            icon={<Plus size={14} />}
            onClick={() => {
              handleAddToPlaylist(previewItem);
              setPreviewItem(null);
            }}
            style={{
              borderRadius: "8px",
              background: "#6366f1",
              borderColor: "#6366f1",
            }}
          >
            {t.addToPlaylist}
          </Button>,
          <Button
            key="close"
            onClick={() => setPreviewItem(null)}
            style={{ borderRadius: "8px" }}
          >
            {t.close}
          </Button>,
        ]}
        width={640}
        centered
      >
        {previewItem && (
          <div
            style={{
              background: "#0f172a",
              borderRadius: "8px",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "300px",
            }}
          >
            {previewItem.type === "video" ? (
              <video
                src={previewItem.url || previewItem.previewUrl}
                controls
                autoPlay
                muted
                style={{ width: "100%", maxHeight: "400px" }}
                onLoadedMetadata={(e) =>
                  handleVideoMetadataLoaded(previewItem.id, e)
                }
              />
            ) : (
              <img
                src={previewItem.url || previewItem.previewUrl}
                alt={previewItem.name}
                style={{
                  width: "100%",
                  maxHeight: "400px",
                  objectFit: "contain",
                }}
              />
            )}
          </div>
        )}
        {previewItem && (
          <div style={{ marginTop: "16px", display: "flex", gap: "24px" }}>
            <div>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                {t.type}
              </Text>
              <br />
              <Text
                strong
                style={{ fontSize: "13px", textTransform: "capitalize" }}
              >
                {previewItem.type}
              </Text>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                {t.size}
              </Text>
              <br />
              <Text strong style={{ fontSize: "13px" }}>
                {previewItem.size}
              </Text>
            </div>
            {previewItem.type === "video" && (
              <div>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {t.duration}
                </Text>
                <br />
                <Text strong style={{ fontSize: "13px" }}>
                  {previewItem.duration}
                </Text>
              </div>
            )}
            <div>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                {t.uploaded}
              </Text>
              <br />
              <Text strong style={{ fontSize: "13px" }}>
                {previewItem.uploadedAt || "-"}
              </Text>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Rename Modal ── */}
      <Modal
        title={t.renameTitle}
        open={!!renameItem}
        onCancel={closeRename}
        okText={t.renameOk}
        cancelText={t.renameCancel}
        onOk={handleRenameConfirm}
        confirmLoading={renameLoading}
        okButtonProps={{
          style: { background: "#6366f1", borderColor: "#6366f1" },
        }}
        centered
      >
        <Input
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onPressEnter={handleRenameConfirm}
          placeholder={t.renamePlaceholder}
          autoFocus
          style={{ borderRadius: "8px" }}
        />
      </Modal>
    </motion.div>
  );
};

export default ContentLibrary;
